import type { CSSObject } from "@emotion/react";

/**
 * A gradient colour stop. A number is a percentage of the gradient line, so the
 * ramp stretches with the element — two cards of different heights end up
 * different colours at the same pixel offset. A CSS length (`"600px"`) pins the
 * ramp to a fixed distance instead, so equal offsets read as equal colour
 * across cards of any size.
 */
type GradientStop = number | string;

const toGradientStop = (stop: GradientStop) =>
  typeof stop === "number" ? `${stop}%` : stop;

type GlassSurfaceOptions = {
  borderRadius?: number;
  backgroundColor: string;
  boxShadow?: string;
  topHighlight: string;
  bottomShade: string;
  gradientAngle?: number;
  highlightStart?: GradientStop;
  highlightEnd?: GradientStop;
  overlayInset?: number;
};

const getPseudoElementBorderRadius = (value?: number) => {
  const maxValue = Math.max(value ? value - 1 : 0, 0);
  return `${maxValue}px`;
};

export const createGlassSurfaceStyles = ({
  backgroundColor,
  borderRadius,
  bottomShade,
  boxShadow,
  gradientAngle = 320,
  highlightEnd = 70,
  highlightStart = 0,
  overlayInset = 0,
  topHighlight
}: GlassSurfaceOptions): CSSObject => {
  return {
    "&::after": {
      background: `linear-gradient(${gradientAngle}deg, ${topHighlight} ${toGradientStop(
        highlightStart
      )}, ${bottomShade} ${toGradientStop(highlightEnd)})`,
      borderRadius: getPseudoElementBorderRadius(borderRadius),
      content: '""',
      inset: overlayInset,
      pointerEvents: "none",
      position: "absolute",
      zIndex: 1
    },
    "& > *": {
      position: "relative",
      zIndex: 2
    },
    background: backgroundColor,
    backgroundImage: "none",
    borderRadius: `${borderRadius ?? 0}px`,
    boxShadow,
    overflow: "hidden"
  };
};
